import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useCompanyFlow } from "@/hooks/useCompanyFlow";
import { Button, Spinner, Stack } from "react-bootstrap";
import toast from "react-hot-toast";
import * as Sentry from "@sentry/react";

import { useMachine } from "@/_zustand/machine";
import { useAuth } from "@/_zustand/auth";
import { useDeleteOneClickCard } from "@/hooks/useDeleteOneClickCard";
import { useOneClickComercio } from "@/hooks/useOneClickComercio";
import { MSG_TARJETA_OTRO_COMERCIO } from "@/lib/oneclickCards";
import {
  adquirirCandadoPago,
  liberarCandadoPago,
  esTokenVigente,
  debeReportarBloqueo,
  bloqueosAcumulados,
  generarBuyOrderOneClick,
  PAYMENT_MSG_EN_CURSO,
} from "@/lib/paymentGuard";
import {
  useAuthorizePayOneClick,
  useReverseOneClickTransaction,
  useCreateOrder,
} from "@/hooks/useTransbank";
import { useCreateTransaccion } from "@/hooks/useTransaccion";
import { useProductStore } from "@/_zustand/store";
import { useOrder } from "@/_zustand/order";
import { mapCartForOrder, buildAppliedDiscount } from "@/lib";
import { filtrarCredencialesParaBody } from "@/lib/transbankCredenciales";

import Cards from "react-credit-cards-2";
import "react-credit-cards-2/dist/lib/styles.scss";

import redCompra from "@/assets/imgs/redCompra.png";
import visa from "@/assets/imgs/visa.png";
import mastercard from "@/assets/imgs/logo-Mastercard.png";
import prepago from "@/assets/imgs/tarjeta-de-credito.png";

export const CardOneClick = () => {
  const navigate = useNavigate();
  const { getCompanyRoute } = useCompanyFlow();
  const { machineSelected } = useMachine();
  const {
    total,
    discount,
    products,
    setBuyOrder,
    setFinishOrder,
    setPayStatus,
    descuento_id,
    descuento_data,
  } = useProductStore();
  const { setOrder } = useOrder();
  const authorizePayOneClick = useAuthorizePayOneClick();
  const createOrder = useCreateOrder();
  const createTransaccion = useCreateTransaccion();
  const reverseTransaction = useReverseOneClickTransaction();
  const totalPedido = useRef(0);
  const confirmTransactionResp = useRef([]);
  const {
    user,
    tempCard,
    setTempCard,
    authType,
    setCards,
    incrementTotalPedidos,
  } = useAuth();
  const { confirmAndDeleteCard, isDeleting } = useDeleteOneClickCard();
  const { comercioEfectivo, tarjetasValidas, tempCardValida, credenciales } =
    useOneClickComercio();
  const [selectedCardId, setSelectedCardId] = useState(0);
  const [pagando, setPagando] = useState(false);
  const buyOrderIntento = useRef(null);
  const reversaLanzada = useRef(false);
  // Token de ownership del candado (ver paymentGuard.js): se guarda en ref
  // porque authorizePay/sendCreateTransaccion/sendPedido/
  // reverseOneClickTransaction son funciones separadas del componente que
  // comparten el mismo intento de pago en curso, no closures anidados.
  const candadoTokenRef = useRef(null);
  // Credenciales del intento que autorizó (borde 3): la reversa debe usar
  // SIEMPRE estas, nunca `machine` "fresco" (podría haber cambiado si el
  // usuario abrió otra tienda en otra pestaña entre autorizar y reversar).
  const credencialesIntentoRef = useRef(null);
  const borderColor = "#c9d4b1";

  const handleRemoveCart = (card) => {
    confirmAndDeleteCard(card, () => {
      setCards((prev) =>
        prev.filter(
          (c) =>
            !(
              c.tbk_user === card.tbk_user &&
              c.codigo_comercio === card.codigo_comercio
            ),
        ),
      );
      navigate(getCompanyRoute("cart"), { replace: true });
    });
  };

  const handleSelectedCard = (card) => {
    if (card && card.usuario_oneclick_id) {
      // ⭐️ CLAVE: Asegurar que el ID guardado sea siempre un NÚMERO
      const idToSet = parseInt(card.usuario_oneclick_id, 10);

      // 1. Guardar la tarjeta seleccionada
      setTempCard(card);

      // 2. 🔑 Disparar la re-renderización con el ID seleccionado
      setSelectedCardId(idToSet);

      // 3. Llamar a las funciones de negocio
      //calculateTotals(machineSelected);
      // authorizePay(); // Si authPay debe ejecutarse inmediatamente
    } else {
      // Maneja el caso en que el ID no está presente
      console.error(
        "El objeto de la tarjeta no tiene un 'usuario_oneclick_id'.",
      );
    }
  };

  useEffect(() => {
    // 1. La condición debe ser simple: ¿Hay un ID válido y seleccionado?
    if (selectedCardId) {
      authorizePay();
    }
    // 3. ⭐️ Dependencia: Se ejecuta cada vez que selectedCardId cambia
    // authorizePay se recrea en cada render (no es un mutateAsync estable);
    // el efecto debe dispararse únicamente cuando cambia selectedCardId, no
    // en cada render, por eso se omite intencionalmente de las deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCardId]);

  const authorizePay = () => {
    // Validación de último momento: un tempCard de otro comercio (dato
    // heredado, dos pestañas, TBK_TOKEN de retorno con máquina cambiada,
    // etc.) nunca debe llegar a Transbank — falla con 422 garantizado.
    // Se evalúa ANTES del candado (AC 20/edge 13): no debe consumirlo.
    if (!tempCardValida) {
      toast.error(MSG_TARJETA_OTRO_COMERCIO);
      setTempCard({});
      navigate(getCompanyRoute("cart"), { replace: true });
      return;
    }

    const totalFinal = Math.max(0, total - discount);

    // Edge 12: el corte por monto $0 tampoco debe consumir el candado.
    if (totalFinal <= 0) {
      toast.error("El monto a pagar no puede ser $0");
      navigate(getCompanyRoute("cart"), { replace: true });
      return;
    }

    // Candado de pago en curso (AC 1/2/3): si ya hay una autorización en
    // vuelo para este flujo, no se dispara un segundo mutateAsync. Se
    // adquiere ANTES de cualquier await (patrón MetodoPagoScreen.js móvil).
    const candado = adquirirCandadoPago("oneclick");
    if (!candado) {
      toast.error(PAYMENT_MSG_EN_CURSO);
      if (debeReportarBloqueo("oneclick")) {
        Sentry.captureMessage(
          "Segundo intento de pago bloqueado por candado",
          {
            level: "warning",
            tags: { payment_flow: "oneclick" },
            extra: {
              buyOrder: buyOrderIntento.current,
              motivo: "candado_en_curso",
              bloqueos: bloqueosAcumulados("oneclick"),
              tbk_user_len: String(tempCard?.tbk_user ?? "").length,
            },
          },
        );
      }
      return;
    }
    candadoTokenRef.current = candado;

    setPagando(true);
    reversaLanzada.current = false;

    // Se capturan las credenciales de este intento AHORA, antes del await:
    // la reversa (borde 3) debe usar exactamente estas, no `machine` "fresco"
    // en el momento de reversar.
    credencialesIntentoRef.current = credenciales;

    // buyOrder regenerado por intento (AC 11/12): usar SIEMPRE la variable
    // local `bo` (nunca el `buyOrder` del store, que sigue stale en este
    // mismo tick de React).
    const bo = generarBuyOrderOneClick();
    buyOrderIntento.current = bo;
    setBuyOrder(bo);

    const body = {
      email: user.usuario_correo,
      buyOrder: bo,
      installmentsNumber: 1,
      total: totalFinal,
      token: tempCard.tbk_user,
      // §0.1 del plan: el backend destructura `apiKey` (no `apikey`); enviar
      // `apikey` en minúscula hacía que esta autorización cayera siempre al
      // fallback por maquina_id — fix declarado, cambio de comportamiento.
      ...filtrarCredencialesParaBody({
        codeComerce: comercioEfectivo,
        codeStore: credenciales.codeStore,
        apiKey: credenciales.apiKey,
      }),
      maquina_id: machineSelected,
    };

    authorizePayOneClick
      .mutateAsync(body)
      .then((resp) => {
        if (resp?.status === "error" || !resp?.data?.details?.[0]) {
          // Segunda capa de ownership (ver paymentGuard.js): esta respuesta
          // puede ser la llegada tardía de un intento cuyo candado ya
          // expiró por TTL y fue re-adquirido por un reintento más nuevo. Si
          // ya no es el dueño vigente, se descarta silenciosamente en vez de
          // expulsar al usuario al carrito por un rechazo que no le
          // corresponde a la compra que ya completó con el intento nuevo.
          if (!esTokenVigente("oneclick", candadoTokenRef.current)) {
            Sentry.captureMessage(
              "Respuesta tardía de un intento de pago obsoleto descartada",
              {
                level: "warning",
                tags: { payment_flow: "oneclick" },
                extra: {
                  buyOrder: bo,
                  maquina_id: machineSelected,
                  motivo: "token_candado_ya_no_vigente",
                },
              },
            );
            return;
          }
          // Rechazo real del backend/Transbank (ej. 422 de comercio o
          // username): antes se perdía por completo (Api.post descartaba el
          // body). Sin financial_risk: el 422 ocurre antes de autorizar, no
          // hay cobro. Nunca se envía el tbk_user completo, sólo su longitud.
          Sentry.captureMessage(
            "Rechazo de authorizePayOneClick (OneClick)",
            {
              level: "error",
              tags: { payment_flow: "oneclick" },
              extra: {
                buyOrder: bo,
                maquina_id: machineSelected,
                codigo_comercio: comercioEfectivo,
                tbk_user_len: String(tempCard?.tbk_user ?? "").length,
                backendMessage: resp?.message,
              },
            },
          );
          setPayStatus(false);
          liberarCandadoPago("oneclick", candadoTokenRef.current);
          setPagando(false);
          setSelectedCardId(0);
          toast.error("Error al realizar el pago");
          return navigate(getCompanyRoute("cart"), { replace: true });
        }

        const data = resp.data;
        if (data.details[0].status == "AUTHORIZED") {
          // Segunda capa de ownership (ver paymentGuard.js): esta respuesta
          // puede ser la llegada tardía de un intento cuyo candado ya
          // expiró por TTL y fue re-adquirido por un reintento más nuevo. Si
          // ya no es el dueño vigente, se descarta silenciosamente esta
          // rama en vez de seguir a sendCreateTransaccion/sendPedido/
          // createOrder con datos obsoletos (evita un doble pedido/cobro).
          if (!esTokenVigente("oneclick", candadoTokenRef.current)) {
            Sentry.captureMessage(
              "Respuesta tardía de un intento de pago obsoleto descartada",
              {
                level: "warning",
                tags: { payment_flow: "oneclick" },
                extra: {
                  buyOrder: bo,
                  maquina_id: machineSelected,
                  motivo: "token_candado_ya_no_vigente",
                },
              },
            );
            return;
          }
          setPayStatus(true);
          sendCreateTransaccion(data);
        } else {
          // Misma segunda capa de ownership que la rama de éxito: un rechazo
          // tardío de un intento obsoleto (candado ya re-adquirido por un
          // intento más nuevo) no debe expulsar al usuario al carrito.
          if (!esTokenVigente("oneclick", candadoTokenRef.current)) {
            Sentry.captureMessage(
              "Respuesta tardía de un intento de pago obsoleto descartada",
              {
                level: "warning",
                tags: { payment_flow: "oneclick" },
                extra: {
                  buyOrder: bo,
                  maquina_id: machineSelected,
                  motivo: "token_candado_ya_no_vigente",
                },
              },
            );
            return;
          }
          setPayStatus(false);
          liberarCandadoPago("oneclick", candadoTokenRef.current);
          setPagando(false);
          setSelectedCardId(0);
          toast.error("El pago fue rechazado por el banco");
          return navigate(getCompanyRoute("cart"), { replace: true });
        }
      })
      .catch((error) => {
        // Misma segunda capa de ownership: el error de red de un intento
        // obsoleto (candado ya re-adquirido por un intento más nuevo) no
        // debe expulsar al usuario al carrito.
        if (!esTokenVigente("oneclick", candadoTokenRef.current)) {
          Sentry.captureMessage(
            "Respuesta tardía de un intento de pago obsoleto descartada",
            {
              level: "warning",
              tags: { payment_flow: "oneclick" },
              extra: {
                buyOrder: bo,
                maquina_id: machineSelected,
                motivo: "token_candado_ya_no_vigente",
              },
            },
          );
          return;
        }
        Sentry.captureException(error, {
          tags: { payment_flow: "oneclick" },
          extra: {
            buyOrder: bo,
            maquina_id: machineSelected,
            codigo_comercio: comercioEfectivo,
            tbk_user_len: String(tempCard?.tbk_user ?? "").length,
          },
        });
        setPayStatus(false);
        liberarCandadoPago("oneclick", candadoTokenRef.current);
        setPagando(false);
        setSelectedCardId(0);
        toast.error("Error al realizar el pago");
        return navigate(getCompanyRoute("cart"), { replace: true });
      });
  };

  const sendCreateTransaccion = (data) => {
    let formData = {
      usuario_id: authType == "user" ? user.usuario_id : 1,
      vci: "",
      amount: data.details[0].amount,
      status: data.details[0].status,
      buy_order: data.buy_order,
      session_id: "",
      card_number: data.card_detail.card_number,
      accounting_date: data.accounting_date,
      transaction_date: data.transaction_date,
      authorization_code: data.details[0].authorization_code,
      payment_type_code: data.details[0].payment_type_code,
      response_code: data.details[0].response_code,
      installments_number: data.details[0].installments_number,
      commerce_code: data.details[0].commerce_code,
      prepaid_balance: data.details[0].prepaid_balance,
      device: "web",
      token: "",
      payment: "oneclick",
    };
    createTransaccion
      .mutateAsync(formData)
      .then((resp) => {
        confirmTransactionResp.current = formData;
        totalPedido.current = resp.amount;
        sendPedido();
      })
      .catch(() => {
        reverseOneClickTransaction();
        toast.error("Error al crear la transacción");
      });
  };

  const reverseOneClickTransaction = () => {
    // AC 10/edge 5: la reversa se ejecuta una sola vez por autorización,
    // incluso si el usuario interactúa durante el proceso.
    if (reversaLanzada.current) return;
    reversaLanzada.current = true;

    setPayStatus(false);

    // Borde 3/AC-11: usar SIEMPRE las credenciales capturadas en el momento
    // de autorizar (credencialesIntentoRef), nunca `machine` "fresco" — si
    // el usuario cambió de máquina en otra pestaña entre autorizar y
    // reversar, `machine` ya no representa el comercio que autorizó. Si el
    // ref está vacío (reversa sin autorización previa en este montaje), cae
    // a la resolución en vivo y reporta un warning; la reversa NUNCA se
    // aborta por falta de credenciales (P2 — dejaría un cobro sin reversar).
    let cred = credencialesIntentoRef.current;
    if (!cred) {
      cred = credenciales;
      Sentry.captureMessage(
        "Reversa OneClick sin credenciales de intento capturadas, usando resolución en vivo",
        {
          level: "warning",
          tags: { payment_flow: "oneclick" },
          extra: { buyOrder: buyOrderIntento.current, maquina_id: machineSelected },
        },
      );
    }

    let formData = {
      buyOrder: buyOrderIntento.current,
      codigoTienda: cred.codeStore,
      total: total,
      maquina_id: machineSelected,
      ...filtrarCredencialesParaBody({
        codeComerce: cred.codeComerce,
        codeStore: cred.codeStore,
        apiKey: cred.apiKey,
      }),
    };
    reverseTransaction
      .mutateAsync(formData)
      .then(() => {
        liberarCandadoPago("oneclick", candadoTokenRef.current);
        setPagando(false);
        setSelectedCardId(0);
        return navigate(getCompanyRoute("cart"), { replace: true });
      })
      .catch((error) => {
        // Riesgo financiero: el cobro pudo haber quedado hecho y la reversa
        // (el intento de anularlo) falló — necesita revisión manual.
        Sentry.captureException(error, {
          level: "error",
          tags: { financial_risk: true, payment_flow: "oneclick" },
          extra: { buyOrder: buyOrderIntento.current, maquina_id: machineSelected },
        });
        liberarCandadoPago("oneclick", candadoTokenRef.current);
        setPagando(false);
        setSelectedCardId(0);
        toast.error("Error al reversar la transacción");
        return navigate(getCompanyRoute("cart"), { replace: true });
      });
  };

  const sendPedido = () => {
    let dataSend = {
      appPlatform: "web",
      userId: user.usuario_id,
      cart: mapCartForOrder(products, machineSelected),
      storeId: machineSelected,
      total: totalPedido.current,
      buyOrder: buyOrderIntento.current,
      appliedDiscount: buildAppliedDiscount(descuento_data, descuento_id),

      transactionDetail: {
        details: [
          {
            amount: confirmTransactionResp.current.amount,
            status: confirmTransactionResp.current.status,
            authorization_code:
              confirmTransactionResp.current.authorization_code,
            payment_type_code: confirmTransactionResp.current.payment_type_code,
            response_code: confirmTransactionResp.current.response_code,
            installments_number:
              confirmTransactionResp.current.installments_number,
            commerce_code: confirmTransactionResp.current.commerce_code,
            buy_order: buyOrderIntento.current,
          },
        ],
        card_detail: {
          card_number: confirmTransactionResp.current.card_number,
        },
        accounting_date: confirmTransactionResp.current.accounting_date,
        transaction_date: confirmTransactionResp.current.transaction_date,
      },
    };

    createOrder
      .mutateAsync(dataSend)
      .then((resp) => {
        //MANEJAR ERROR
        if (resp.status === "success") {
          // Camino de éxito (AC 6): el candado NO se libera; la navegación a
          // /order es el único desenlace posible desde aquí.
          setOrder({ ...resp, pedido_total: totalPedido.current });
          setFinishOrder(machineSelected);
          toast.success("EXITO");
          incrementTotalPedidos();
          return navigate(getCompanyRoute("order"), { replace: true });
        } else {
          liberarCandadoPago("oneclick", candadoTokenRef.current);
          setPagando(false);
          setSelectedCardId(0);
          toast.error(resp.message);
          return navigate(getCompanyRoute("cart"), { replace: true });
        }
      })
      .catch((error) => {
        console.log(error);
        toast.error("Error al realizar el pedido");
        reverseOneClickTransaction();
      });
  };

  if (tarjetasValidas.length > 0) {
    return (
      <div className="row">
        {pagando && (
          <div className="col-12 mb-3 d-flex align-items-center" role="status">
            <Spinner
              animation="border"
              size="sm"
              className="me-2"
              aria-hidden="true"
            ></Spinner>
            <span className="inter-regular">{PAYMENT_MSG_EN_CURSO}</span>
            <Button
              variant="link"
              className="ms-2"
              onClick={() => navigate(getCompanyRoute("cart"), { replace: true })}
            >
              Volver al carrito
            </Button>
          </div>
        )}
        {tarjetasValidas.map((card, index) => {
          const cardColSize =
            tarjetasValidas.length === 1 ? "col-12" : "col-md-6 mb-3";

          // ⭐️ Comparación limpia y consistente
          const isSelected =
            card.usuario_oneclick_id === tempCard.usuario_oneclick_id;
          return (
            <div
              key={card.card_id || `card-${index}`}
              // ⭐️ APLICACIÓN DEL ESTILO CONDICIONAL ⭐️

              // Mantén solo las clases de layout y texto en className
              className={`inter-regular text-black rounded p-3 d-flex justify-content-between align-items-center ${cardColSize}`}
              onClick={pagando ? undefined : () => handleSelectedCard(card)}
              aria-disabled={pagando}
              style={{
                pointerEvents: pagando ? "none" : undefined,
                opacity: pagando ? 0.6 : 1,
                cursor: pagando ? "default" : "pointer",
              }}
            >
              {/* Contenedor de la información de la tarjeta (sin onClick aquí) */}
              <div
                className="d-flex align-items-center"
                style={
                  isSelected
                    ? {
                        boxShadow: "0 10px 28px #343a40", // Fondo muy claro
                        border: `2px solid ${borderColor}`, // Borde suave para marcar la selección
                      }
                    : {
                        border: "1px solid #dee2e6", // Borde normal (usando color de Bootstrap)
                      }
                }
              >
                <Stack direction="horizontal" gap={3}>
                  <Cards
                    number={card.card_number}
                    expiry={""}
                    cvc={""}
                    name={card.card_type}
                    focused={"name"} // Pasa la propiedad 'focus'
                    // Renderiza el número de la tarjeta en una máscara
                    placeholders={{ name: "NOMBRE EN TARJETA" }}
                  />

                  <button
                    className="waller-delete-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveCart(card);
                    }}
                    disabled={isDeleting || pagando}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </Stack>
              </div>

              {/* Contenedor del botón de eliminar */}
              {/* <div>
                {deletingInscripcion || deletingOneClick ? (
                  <Spinner
                    animation="border"
                    size="sm"
                    className="me-2"
                    role="status"
                    aria-hidden="true"
                  ></Spinner>
                ) : (
                  <Button
                    variant="outline-danger"
                    className="ms-2"
                    onClick={(e) => {
                      e.stopPropagation(); // Detiene el clic en este botón para que no afecte al padre
                      handleRemoveCart(card);
                    }}
                  >
                    <i className="fas fa-trash"></i>
                  </Button>
                )}
              </div> */}
            </div>
          );
        })}
      </div>
    );
  }
  if (!tempCardValida) {
    // Criterio 4: un tempCard de otro comercio (o inválido) no se muestra;
    // el componente no pinta ninguna tarjeta, dejando paso al alta.
    return null;
  }
  return (
    <div className="col-12 inter-regular text-black font16 border border-1 rounded mb-3 p-3 d-flex justify-content-lg-between">
      <Stack direction="horizontal" gap={3}>
        <p className="mt-2">
          {tempCard.card_type == "Visa" && (
            <img src={visa} alt="webpay" width="40" />
          )}
          {tempCard.card_type == "Master" && (
            <img src={mastercard} alt="webpay" width="40" />
          )}
          {tempCard.card_type == "RedCompra" && (
            <img src={redCompra} alt="webpay" width="40" />
          )}
          {tempCard.card_type == "Prepago" && (
            <img src={prepago} alt="webpay" width="40" />
          )}
        </p>
        <p className="mt-3 me-auto">
          <span className="mx-4">{tempCard.card_number}</span>
        </p>
      </Stack>
      <div className="row d-flex mt-3">
        <div className="col-12 text-end">
          <div className="d-flex align-items-center font14">
            {isDeleting ? (
              <Spinner
                animation="border"
                size="sm"
                className="me-2"
                role="status"
                aria-hidden="true"
              ></Spinner>
            ) : (
              <Button
                variant="outline-danger"
                className="ms-2"
                onClick={() => handleRemoveCart(tempCard)}
              >
                <i className="fas fa-trash"></i>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
